import { useEffect, useRef } from 'react';
import type { EChartsType } from 'echarts';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

interface IUseRobloxLineChartTooltipFixOptions {
  chartRef: React.RefObject<IChartsIndexInstance | null>;
  wrapperRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
  deps?: unknown[];
}

const hideChartTooltip = (chart?: EChartsType | null): void => {
  if (!chart || chart.isDisposed?.()) {
    return;
  }

  try {
    chart.dispatchAction({ type: 'hideTip' });
  } catch (error) {
    console.debug('[RobloxLineChart] hideTip failed', error);
  }
};

const getChartInstance = (
  chartRef: React.RefObject<IChartsIndexInstance | null>,
): EChartsType | null => {
  const chart = chartRef.current?.getLineInstance?.();
  if (!chart || chart.isDisposed?.()) {
    return null;
  }
  return chart;
};

/** 修复 appendToBody tooltip 移出图表后残留、重入出现双弹窗的问题 */
export const useRobloxLineChartTooltipFix = (
  options: IUseRobloxLineChartTooltipFixOptions,
): void => {
  const { chartRef, wrapperRef, enabled = true, deps = [] } = options;
  const handlersRef = useRef<{
    chart: EChartsType;
    onHide: () => void;
    container: HTMLElement | null;
    wrapper: HTMLElement | null;
  } | null>(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let rafId = 0;
    let retryTimer = 0;

    const cleanup = (): void => {
      const handlers = handlersRef.current;
      if (!handlers) {
        return;
      }

      hideChartTooltip(handlers.chart);
      handlers.chart.off('globalout', handlers.onHide);
      handlers.container?.removeEventListener('mouseleave', handlers.onHide);
      handlers.wrapper?.removeEventListener('mouseleave', handlers.onHide);
      handlersRef.current = null;
    };

    const bind = (attempt = 0): void => {
      cleanup();

      const chart = getChartInstance(chartRef);
      const container = chart?.getDom?.() || null;
      const wrapper = wrapperRef?.current || null;

      if (!chart) {
        if (attempt < 20) {
          retryTimer = window.setTimeout(() => bind(attempt + 1), 50);
        }
        return;
      }

      const onHide = (): void => {
        hideChartTooltip(chart);
      };

      chart.on('globalout', onHide);
      container?.addEventListener('mouseleave', onHide);
      wrapper?.addEventListener('mouseleave', onHide);

      handlersRef.current = {
        chart,
        onHide,
        container,
        wrapper,
      };
    };

    rafId = window.requestAnimationFrame(() => bind());

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(retryTimer);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, wrapperRef, enabled, ...deps]);
};
